import {ActionIcon, Anchor, Avatar, Badge, Button, Group, Paper, Stack, Text} from '@mantine/core'
import {IconLink, IconMinus, IconTruck, IconUser} from '@tabler/icons-react'

import {GroupedEquipmentAssignmentCard} from 'src/equipment/grouped-equipment-assignment-card'
import {formatGroupedEquipmentName} from 'src/equipment/stock-equipment'
import {
    actionDestructiveColor,
    identityEquipmentColor,
    identityWorkerColor,
    signalAttentionColor,
    signalPositiveTextColor,
} from 'src/theme/semantic-colors'

import classes from './dispatch-assignment-panel.module.css'
import {DRIVER_SEND_STATUS} from './driver-send-status'
import {rerentNameParts} from './rerent-name'
import type {DispatchTimelineData, JobRequirement} from './types'

type DispatchAssignedResourceCardProps = {
    resourceId: string
    resourceName: string
    quantity?: number
    requirement: JobRequirement
    displayData: DispatchTimelineData
    fitReasons?: string[]
    alsoBookedLabel?: string
    bookedDaysLabel?: string
    /** Home yard of a worker borrowed from another yard. */
    borrowedFrom?: string
    sent?: boolean
    onMarkSent?: () => void
    isMarkingSent?: boolean
    onCopyLink?: () => void
    isCopyingLink?: boolean
    onRemove?: () => void
    disabled?: boolean
    jobPageHref?: string
}

function RerentName({
    parts,
    jobPageHref,
}: {
    parts: {vendor: string; type: string; unit: string | null}
    jobPageHref?: string
}) {
    return (
        <Stack gap={0} style={{minWidth: 0, flex: 1}}>
            {parts.unit ? (
                <Text size="sm" fw={600} style={{overflowWrap: 'anywhere'}}>
                    Unit {parts.unit}
                </Text>
            ) : jobPageHref ? (
                <Anchor href={jobPageHref} size="sm" fw={600}>
                    Add unit # on the job page
                </Anchor>
            ) : (
                <Text size="sm" fw={600}>
                    No unit # yet
                </Text>
            )}
            <Text size="xs" c="dimmed" style={{overflowWrap: 'anywhere'}}>
                {parts.vendor} — {parts.type}
            </Text>
        </Stack>
    )
}

function DriverNotifiedLabel({sent}: {sent: boolean}) {
    if (!sent) {
        return null
    }

    return (
        <Text size="xs" c={signalPositiveTextColor()} fw={500}>
            {DRIVER_SEND_STATUS.notified}
        </Text>
    )
}

export function DispatchAssignedResourceCard({
    resourceId,
    resourceName,
    quantity,
    requirement,
    displayData,
    fitReasons = [],
    alsoBookedLabel,
    bookedDaysLabel,
    borrowedFrom,
    sent = false,
    onMarkSent,
    isMarkingSent = false,
    onCopyLink,
    isCopyingLink = false,
    onRemove,
    disabled = false,
    jobPageHref,
}: DispatchAssignedResourceCardProps) {
    const assignedAsset =
        requirement.kind === 'rental_asset'
            ? displayData.equipment.find((asset) => asset.id === resourceId)
            : undefined
    const locationLine = assignedAsset?.locationLabel
        ? `At ${assignedAsset.locationLabel}`
        : undefined
    const fitReason = fitReasons[0] ?? locationLine

    if (requirement.kind === 'rental_asset' && quantity != null && quantity > 1) {
        return (
            <Stack gap={4} data-testid={`assigned-card-${resourceId}`}>
                <GroupedEquipmentAssignmentCard
                    resourceName={resourceName}
                    quantity={quantity}
                    disabled={disabled}
                    onRemove={onRemove}
                />
                {fitReason ? (
                    <Text size="xs" c="dimmed" pl="sm">
                        {fitReason}
                    </Text>
                ) : null}
            </Stack>
        )
    }

    const icon = requirement.kind === 'worker' ? <IconUser size={14} /> : <IconTruck size={14} />
    const displayName =
        quantity != null && quantity > 1
            ? formatGroupedEquipmentName(resourceName, quantity)
            : resourceName
    const rerent = requirement.kind === 'rental_asset' ? rerentNameParts(resourceName) : null
    const showMarkNotified = requirement.kind === 'worker' && !sent && onMarkSent != null
    const avatarColor =
        requirement.kind === 'worker'
            ? sent
                ? 'green'
                : identityWorkerColor()
            : identityEquipmentColor()

    return (
        <Paper
            withBorder
            p="sm"
            radius="md"
            className={classes['assignedCard']}
            data-testid={`assigned-card-${resourceId}`}
        >
            <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <Group gap="sm" wrap="nowrap" align="center" style={{minWidth: 0, flex: 1}}>
                    <Avatar radius="xl" size="sm" color={avatarColor}>
                        {icon}
                    </Avatar>
                    {requirement.kind === 'worker' ? (
                        <Stack gap={2} style={{minWidth: 0, flex: 1}}>
                            <Text size="sm" fw={600} truncate>
                                {displayName}
                            </Text>
                            {borrowedFrom ? (
                                <Badge
                                    size="xs"
                                    variant="light"
                                    color="grape"
                                    w="fit-content"
                                    data-testid="borrowed-from-badge"
                                >
                                    From {borrowedFrom}
                                </Badge>
                            ) : null}
                            {bookedDaysLabel ? (
                                <Text size="xs" c="dimmed" data-testid="booked-days-label">
                                    {bookedDaysLabel}
                                </Text>
                            ) : null}
                            {alsoBookedLabel ? (
                                <Group gap={6} wrap="nowrap">
                                    <Badge size="xs" variant="filled" color="orange">
                                        Booked that day
                                    </Badge>
                                    <Text size="xs" c="orange.8" truncate>
                                        {alsoBookedLabel}
                                    </Text>
                                </Group>
                            ) : null}
                            <DriverNotifiedLabel sent={sent} />
                        </Stack>
                    ) : rerent ? (
                        <RerentName parts={rerent} jobPageHref={jobPageHref} />
                    ) : (
                        <Stack gap={0} style={{minWidth: 0, flex: 1}}>
                            <Text size="sm" fw={600} truncate>
                                {displayName}
                            </Text>
                            {fitReason ? (
                                <Text size="xs" c="dimmed" truncate>
                                    {fitReason}
                                </Text>
                            ) : null}
                        </Stack>
                    )}
                </Group>
                <Group gap={4} wrap="nowrap" style={{flexShrink: 0}}>
                    {requirement.kind === 'worker' && onCopyLink ? (
                        <Button
                            size="compact-xs"
                            variant="default"
                            leftSection={<IconLink size={12} />}
                            loading={isCopyingLink}
                            disabled={disabled}
                            onClick={onCopyLink}
                            aria-label={`Copy link for ${resourceName}`}
                        >
                            {DRIVER_SEND_STATUS.copyDriverLink}
                        </Button>
                    ) : null}
                    {showMarkNotified ? (
                        <Button
                            size="compact-xs"
                            color={signalAttentionColor()}
                            loading={isMarkingSent}
                            disabled={disabled}
                            onClick={onMarkSent}
                        >
                            {DRIVER_SEND_STATUS.markNotified}
                        </Button>
                    ) : null}
                    {onRemove ? (
                        <ActionIcon
                            variant="subtle"
                            color={actionDestructiveColor()}
                            size="sm"
                            aria-label="Remove"
                            disabled={disabled}
                            onClick={onRemove}
                        >
                            <IconMinus size={14} />
                        </ActionIcon>
                    ) : null}
                </Group>
            </Group>
        </Paper>
    )
}
